/**
 * useAutoLogout.js
 *
 * Tracks user activity and automatically logs out after a period of inactivity.
 *
 * Flow:
 *   1. User is active  → timer resets on every mouse move / keypress / click / scroll / touch
 *   2. IDLE_TIMEOUT reached → warning modal appears with a 60-second countdown
 *   3. If user acts during countdown → timer resets, warning dismissed
 *   4. Countdown hits 0 → performLogout() is called
 *
 * Configuration
 * ─────────────
 * IDLE_TIMEOUT_MS  : inactivity period before the warning appears (default 14 min)
 * WARN_COUNTDOWN_S : seconds of the countdown shown in the warning (default 60 s)
 *
 * Usage (in Layout or App):
 *   const { showWarning, countdown, stayLoggedIn } = useAutoLogout({ onLogout });
 */

import { useState, useEffect, useRef, useCallback } from 'react';

const IDLE_TIMEOUT_MS  = 14 * 60 * 1000; // 14 minutes idle → show warning
const WARN_COUNTDOWN_S = 60;              // 60-second countdown before forced logout

function clearTokens() {
  localStorage.removeItem('jwt');
  localStorage.removeItem('token');
  localStorage.removeItem('role');
  localStorage.removeItem('email');
}

export default function useAutoLogout({ onLogout, enabled = true } = {}) {
  const [showWarning, setShowWarning]   = useState(false);
  const [countdown, setCountdown]       = useState(WARN_COUNTDOWN_S);

  const idleTimerRef     = useRef(null);
  const countdownTimerRef = useRef(null);
  const warningActiveRef  = useRef(false);

  // ── Perform the actual logout ─────────────────────────────────────────────
  const performLogout = useCallback(() => {
    clearInterval(countdownTimerRef.current);
    clearTimeout(idleTimerRef.current);
    setShowWarning(false);
    warningActiveRef.current = false;
    clearTokens();
    if (onLogout) {
      onLogout();
    } else {
      window.location.href = '/login';
    }
  }, [onLogout]);

  // ── Start countdown once warning appears ─────────────────────────────────
  const startCountdown = useCallback(() => {
    setCountdown(WARN_COUNTDOWN_S);
    let remaining = WARN_COUNTDOWN_S;
    countdownTimerRef.current = setInterval(() => {
      remaining -= 1;
      setCountdown(remaining);
      if (remaining <= 0) {
        clearInterval(countdownTimerRef.current);
        performLogout();
      }
    }, 1000);
  }, [performLogout]);

  // ── Show the warning modal ────────────────────────────────────────────────
  const showIdleWarning = useCallback(() => {
    if (warningActiveRef.current) return;
    warningActiveRef.current = true;
    setShowWarning(true);
    startCountdown();
  }, [startCountdown]);

  // ── Reset idle timer on any activity ─────────────────────────────────────
  const resetTimer = useCallback(() => {
    // If warning is already showing, dismiss it (user came back)
    if (warningActiveRef.current) {
      clearInterval(countdownTimerRef.current);
      warningActiveRef.current = false;
      setShowWarning(false);
      setCountdown(WARN_COUNTDOWN_S);
    }
    clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(showIdleWarning, IDLE_TIMEOUT_MS);
  }, [showIdleWarning]);

  // ── "Stay logged in" button in the warning modal ──────────────────────────
  const stayLoggedIn = useCallback(() => {
    clearInterval(countdownTimerRef.current);
    warningActiveRef.current = false;
    setShowWarning(false);
    setCountdown(WARN_COUNTDOWN_S);
    // restart idle timer fresh
    clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(showIdleWarning, IDLE_TIMEOUT_MS);
  }, [showIdleWarning]);

  // ── Attach / detach activity listeners ───────────────────────────────────
  useEffect(() => {
    if (!enabled) return;

    // Only run when a JWT exists (user is logged in)
    if (!localStorage.getItem('jwt') && !localStorage.getItem('token')) return;

    const EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    EVENTS.forEach(ev => window.addEventListener(ev, resetTimer, { passive: true }));

    // Kick off the first idle timer
    resetTimer();

    return () => {
      EVENTS.forEach(ev => window.removeEventListener(ev, resetTimer));
      clearTimeout(idleTimerRef.current);
      clearInterval(countdownTimerRef.current);
    };
  }, [enabled, resetTimer]);

  return { showWarning, countdown, stayLoggedIn, performLogout };
}
